/**
 * Find a letter paper's writing area: the largest clear rectangle of the
 * paper's own colour, so words never sit on the art around it. The admin can
 * fine-tune it by dragging; this only makes the first guess.
 *
 * Works on a coarse grid (about 100 cells across) so specks of splatter don't
 * matter: a cell is clear when nearly all of its pixels are close to the paper
 * colour (the median of the centre of the sheet), and a lone speck in clear
 * paper is ignored. The largest all-clear
 * rectangle of cells (the classic histogram method) wins, then a small margin
 * is taken off each side so the words breathe.
 */

export interface Pixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface WritingArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const CELLS_ACROSS = 100;
const NEAR = 48; // |ΔR|+|ΔG|+|ΔB| to the paper colour
const CELL_CLEAR = 0.9;
const MARGIN = 0.025;
const SPECK = 2;
const SPECK_CLEAR = 0.7;

function median(v: number[]): number {
  const s = [...v].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/** The paper colour: the median of a box at the centre of the sheet. */
export function paperColour(img: Pixels): [number, number, number] {
  const { width: w, height: h, data } = img;
  const r: number[] = [];
  const g: number[] = [];
  const b: number[] = [];
  const bw = Math.max(1, Math.floor(w * 0.1));
  const bh = Math.max(1, Math.floor(h * 0.1));
  for (let y = Math.floor(h / 2 - bh / 2); y < h / 2 + bh / 2; y++) {
    for (let x = Math.floor(w / 2 - bw / 2); x < w / 2 + bw / 2; x++) {
      const i = (y * w + x) * 4;
      r.push(data[i]!);
      g.push(data[i + 1]!);
      b.push(data[i + 2]!);
    }
  }
  return [median(r), median(g), median(b)];
}

export function detectWritingArea(img: Pixels): WritingArea {
  const { width: w, height: h, data } = img;
  const paper = paperColour(img);
  const cell = Math.max(1, Math.floor(w / CELLS_ACROSS));
  const cols = Math.floor(w / cell);
  const rows = Math.floor(h / cell);
  const clear = new Uint8Array(cols * rows);
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      let ok = 0;
      let n = 0;
      for (let y = cy * cell; y < (cy + 1) * cell; y++) {
        for (let x = cx * cell; x < (cx + 1) * cell; x++) {
          const i = (y * w + x) * 4;
          const d = Math.abs(data[i]! - paper[0]) + Math.abs(data[i + 1]! - paper[1]) + Math.abs(data[i + 2]! - paper[2]);
          if (d < NEAR) ok++;
          n++;
        }
      }
      clear[cy * cols + cx] = ok / n >= CELL_CLEAR ? 1 : 0;
    }
  }

  // Specks of splatter on the paper are not art: a blocked cell mostly
  // surrounded by clear paper (5×5 cells) counts as clear. The edge of a real
  // band of art is never mostly surrounded, so it stays blocked.
  const filled = new Uint8Array(clear);
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      if (clear[cy * cols + cx]) continue;
      let on = 0;
      let n = 0;
      for (let dy = -SPECK; dy <= SPECK; dy++) {
        for (let dx = -SPECK; dx <= SPECK; dx++) {
          const x = cx + dx;
          const y = cy + dy;
          if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
          on += clear[y * cols + x]!;
          n++;
        }
      }
      if (on / n >= SPECK_CLEAR) filled[cy * cols + cx] = 1;
    }
  }
  clear.set(filled);

  // Largest all-clear rectangle: per row, the largest rectangle under the histogram.
  const heights = new Int32Array(cols);
  let best = { area: 0, top: 0, bottom: rows, left: 0, right: cols };
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) heights[x] = clear[y * cols + x] ? heights[x]! + 1 : 0;
    const stack: number[] = [];
    for (let x = 0; x <= cols; x++) {
      const hx = x < cols ? heights[x]! : 0;
      while (stack.length && heights[stack[stack.length - 1]!]! >= hx) {
        const top = stack.pop()!;
        const height = heights[top]!;
        const left = stack.length ? stack[stack.length - 1]! + 1 : 0;
        const area = height * (x - left);
        if (area > best.area) best = { area, top: y - height + 1, bottom: y + 1, left, right: x };
      }
      stack.push(x);
    }
  }

  const area: WritingArea = best.area
    ? {
        top: (best.top * cell) / h + MARGIN,
        bottom: 1 - (best.bottom * cell) / h + MARGIN,
        left: (best.left * cell) / w + MARGIN,
        right: 1 - (best.right * cell) / w + MARGIN,
      }
    : { top: 0.12, right: 0.12, bottom: 0.12, left: 0.12 };
  const round = (n: number) => Math.round(Math.max(0, Math.min(0.9, n)) * 1000) / 1000;
  return { top: round(area.top), right: round(area.right), bottom: round(area.bottom), left: round(area.left) };
}
